import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight, ClipboardList, Loader2, MapPin, Navigation, Play, X } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import Sheet from '@/features/chat/components/Sheet';
import Avatar from '@/features/chat/components/Avatar';
import { ScreenHeader, Spinner } from '@/components/ui';
import { useProfile } from '@/features/profile/useProfile';
import { GeoError, getPosition } from '@/lib/geo';
import { flushQueue } from '@/lib/queue';
import { SHIFT_KEY, writeShiftCache } from '@/lib/shift-cache';
import { formatDuration, formatKop, formatQty, formatWeekday, formatWhen, plural, uid } from '@/lib/format';
import { getMyAnalytics, listLoads, listRoutes, startShift, type AvailableLoad, type CityRoutes } from '@/services/courier';

type Route = CityRoutes['routes'][number];

interface Confirm {
  routeId: string;
  routeName: string;
  cityName: string;
  pointsCount: number | null;
  load: AvailableLoad | null;
}

export function StartScreen() {
  const qc = useQueryClient();
  const { data: me } = useProfile();
  const [step, setStep] = useState<'idle' | 'pick'>('idle');
  const [cityId, setCityId] = useState<string | null>(null);
  const [pendingLoad, setPendingLoad] = useState<AvailableLoad | null>(null);
  const [loadOpen, setLoadOpen] = useState<AvailableLoad | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [starting, setStarting] = useState(false);
  const [geoProblem, setGeoProblem] = useState<string | null>(null);
  const routes = useQuery({ queryKey: ['routes'], queryFn: listRoutes, enabled: step === 'pick', refetchInterval: step === 'pick' ? 20_000 : false });
  const loads = useQuery({ queryKey: ['loads'], queryFn: listLoads, refetchInterval: 60_000 });
  const last = useQuery({
    queryKey: ['stats', 'last-shift'],
    queryFn: () => getMyAnalytics({}),
    select: (d) => d.shifts.find((s) => s.status === 'FINISHED') ?? null,
    staleTime: 60_000,
  });

  const cities = routes.data ?? [];
  // Один город — сразу к маршрутам.
  const city = cities.find((c) => c.id === cityId) ?? (cities.length === 1 ? cities[0] : null);

  const openConfirm = (c: Confirm) => {
    setGeoProblem(null);
    setConfirm(c);
  };

  const pickRoute = (c: CityRoutes, r: Route) =>
    openConfirm({ routeId: r.id, routeName: r.name, cityName: c.name, pointsCount: r.pointsCount, load: pendingLoad });

  const startByLoad = (load: AvailableLoad) => {
    setLoadOpen(null);
    if (load.route && load.route.isActive) {
      openConfirm({ routeId: load.route.id, routeName: load.route.name, cityName: load.route.city.name, pointsCount: null, load });
      return;
    }
    setPendingLoad(load);
    setStep('pick');
  };

  const start = async () => {
    if (!confirm) return;
    setStarting(true);
    setGeoProblem(null);
    try {
      // Без геопозиции точки не отметить — спрашиваем доступ до начала смены.
      try {
        await getPosition(15_000);
      } catch (err) {
        if (err instanceof GeoError && (err.kind === 'denied' || err.kind === 'unsupported')) {
          setGeoProblem(err.message);
          return;
        }
        toast('GPS пока не нашёл вас — проверьте, что геолокация в телефоне включена', { duration: 5000 });
      }
      if (!(await flushQueue())) {
        toast.error('Сначала отправим точки прошлой смены — нужна связь');
        return;
      }
      const res = await startShift(confirm.routeId, uid(), confirm.load?.id);
      writeShiftCache(res.active);
      qc.setQueryData(SHIFT_KEY, res.active);
      void qc.invalidateQueries({ queryKey: ['me'] });
      void qc.invalidateQueries({ queryKey: ['loads'] });
      toast.success(confirm.load ? `Смена по накладной №${confirm.load.number} началась` : 'Смена началась. Хорошей дороги!');
      setConfirm(null);
      setPendingLoad(null);
    } catch {
      void routes.refetch();
      void loads.refetch();
      setConfirm(null);
    } finally {
      setStarting(false);
    }
  };

  const backFromPick = () => {
    if (city && cities.length > 1 && cityId) {
      setCityId(null);
      return;
    }
    setStep('idle');
    setCityId(null);
    setPendingLoad(null);
  };

  const available = loads.data ?? [];

  return (
    <>
      <ScreenHeader
        title={step === 'pick' && city ? city.name : 'Маршрут'}
        subtitle={step === 'pick' ? (city ? 'Выберите маршрут' : 'Выберите город') : me?.wholesaler.name}
        left={
          step === 'pick' ? (
            <button type="button" className="btn-icon" aria-label="Назад" onClick={backFromPick}>
              <ArrowLeft size={20} />
            </button>
          ) : undefined
        }
      />
      <div className="mx-auto w-full max-w-md space-y-3 px-4 py-4">
        {step === 'idle' ? (
          <>
            {available.length > 0 && (
              <section className="card overflow-hidden">
                <h2 className="flex items-center gap-2 px-4 pb-1 pt-3.5 text-[13px] font-medium text-ink/50">
                  <ClipboardList size={15} /> Накладные для вас
                </h2>
                <ul className="list-rows">
                  {available.map((l) => (
                    <li key={l.id}>
                      <button type="button" className="flex w-full items-center gap-3 px-4 py-3.5 text-left active:bg-ink/[0.03]" onClick={() => setLoadOpen(l)}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-ink">Накладная №{l.number}</span>
                            {l.personal && <span className="badge-orange">вам</span>}
                          </div>
                          <div className="truncate text-[13px] text-ink/50">
                            {l.route ? `${l.route.city.name} · ${l.route.name}` : 'Маршрут на выбор'} · {l.lines.length}{' '}
                            {plural(l.lines.length, 'позиция', 'позиции', 'позиций')}
                          </div>
                        </div>
                        <ChevronRight size={18} className="shrink-0 text-ink/30" />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            <section className="card flex flex-col items-center px-6 pb-6 pt-8 text-center">
              <Avatar name={me?.name ?? ' '} url={me?.avatarUrl} size={64} />
              <h2 className="mt-4 text-xl font-semibold tracking-tight text-ink">{me ? `Привет, ${me.name.split(' ')[0]}` : 'Привет'}</h2>
              <p className="mt-1 text-[15px] text-ink/55">
                {available.length > 0 ? 'Начните маршрут по накладной выше или выберите обычный маршрут.' : 'Смена не начата. Выберите город и маршрут — и в путь.'}
              </p>
              <button type="button" className={clsx('mt-6 w-full', available.length > 0 ? 'btn-outline' : 'btn-primary')} onClick={() => setStep('pick')}>
                <Play size={18} fill="currentColor" /> {available.length > 0 ? 'Маршрут без накладной' : 'Начать работу'}
              </button>
            </section>
            {last.data && (
              <section className="card p-4">
                <div className="text-[13px] text-ink/50">
                  Прошлая смена · {formatWeekday(last.data.startedAt)} · {formatDuration(last.data.durationMs)}
                </div>
                <div className="mt-0.5 font-medium text-ink">
                  {last.data.cityName} · {last.data.routeName}
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3 text-[13px]">
                  <div>
                    <div className="text-ink/50">Точки</div>
                    <div className="font-semibold tabular-nums text-ink">
                      {last.data.pointsDone}/{last.data.pointsTotal}
                    </div>
                  </div>
                  <div>
                    <div className="text-ink/50">Продано</div>
                    <div className="font-semibold tabular-nums text-ink">{formatKop(last.data.collectedTotal + last.data.debtTotal)}</div>
                  </div>
                  <div>
                    <div className="text-ink/50">Получено</div>
                    <div className="font-semibold tabular-nums text-ink">{formatKop(last.data.collectedTotal)}</div>
                  </div>
                </div>
              </section>
            )}
          </>
        ) : (
          <>
            {pendingLoad && (
              <div className="flex items-center gap-3 rounded-2xl bg-orange-50 py-2.5 pl-4 pr-2 text-[14px] text-orange-700 ring-1 ring-orange/20">
                <ClipboardList size={17} className="shrink-0" />
                <span className="min-w-0 flex-1">
                  По накладной №{pendingLoad.number} — выберите маршрут
                </span>
                <button type="button" className="btn-icon h-9 w-9 text-orange-700/70" aria-label="Без накладной" onClick={() => setPendingLoad(null)}>
                  <X size={17} />
                </button>
              </div>
            )}
            {routes.isPending ? (
              <Spinner />
            ) : cities.length === 0 ? (
              <div className="card px-6 py-10 text-center">
                <div className="font-medium text-ink">Маршрутов пока нет</div>
                <p className="mt-1 text-sm text-ink/55">Попросите руководителя добавить город, маршрут и точки.</p>
              </div>
            ) : !city ? (
              <ul className="card list-rows overflow-hidden">
                {cities.map((c) => (
                  <li key={c.id}>
                    <button type="button" className="flex w-full items-center gap-3 px-4 py-4 text-left active:bg-ink/[0.03]" onClick={() => setCityId(c.id)}>
                      <MapPin size={20} className="shrink-0 text-ink/40" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-ink">{c.name}</div>
                        <div className="text-[13px] text-ink/50">
                          {c.routes.length} {plural(c.routes.length, 'маршрут', 'маршрута', 'маршрутов')}
                        </div>
                      </div>
                      <ChevronRight size={18} className="text-ink/30" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="card list-rows overflow-hidden">
                {city.routes.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      disabled={!!r.busyBy}
                      className={clsx('flex w-full items-center gap-3 px-4 py-4 text-left', r.busyBy ? 'cursor-not-allowed' : 'active:bg-ink/[0.03]')}
                      onClick={() => pickRoute(city, r)}
                    >
                      <div className="min-w-0 flex-1">
                        <div className={clsx('truncate font-medium', r.busyBy ? 'text-ink/40' : 'text-ink')}>{r.name}</div>
                        <div className="text-[13px] text-ink/50">
                          {r.pointsCount} {plural(r.pointsCount, 'точка', 'точки', 'точек')}
                          {r.comment ? ` · ${r.comment}` : ''}
                        </div>
                      </div>
                      {r.busyBy ? <span className="badge-gray shrink-0">Едет {r.busyBy.name.split(' ')[0]}</span> : <ChevronRight size={18} className="text-ink/30" />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      {loadOpen && (
        <Sheet title={`Накладная №${loadOpen.number}`} onClose={() => setLoadOpen(null)}>
          <div className="text-[13px] text-ink/55">
            Выдана {formatWhen(loadOpen.issuedAt)} · {loadOpen.route ? `${loadOpen.route.city.name}, ${loadOpen.route.name}` : 'маршрут на выбор'}
            {!loadOpen.personal && ' · любому курьеру'}
          </div>
          {loadOpen.comment && <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-[14px] text-amber-900">{loadOpen.comment}</p>}
          <ul className="mt-3 divide-y divide-line rounded-xl ring-1 ring-line">
            {loadOpen.lines.map((l) => (
              <li key={l.itemId} className="flex items-baseline justify-between gap-3 px-3 py-2.5 text-[14px]">
                <span className="min-w-0 text-ink">{l.nameSnapshot}</span>
                <span className="shrink-0 font-medium tabular-nums text-ink">
                  {formatQty(l.issuedQty)} {l.unitSnapshot}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[13px] text-ink/55">Отдавать на точках можно только то, что в накладной. В конце смены пересчитаете остаток.</p>
          <button type="button" className="btn-primary mt-5 w-full" onClick={() => startByLoad(loadOpen)}>
            {loadOpen.route && loadOpen.route.isActive ? `Начать маршрут «${loadOpen.route.name}»` : 'Выбрать маршрут'}
          </button>
          <button type="button" className="btn-ghost mt-2 w-full" onClick={() => setLoadOpen(null)}>
            Закрыть
          </button>
        </Sheet>
      )}

      {confirm && (
        <Sheet title="Начать смену?" onClose={() => !starting && setConfirm(null)}>
          <div className="rounded-xl bg-paper p-4">
            <div className="font-medium text-ink">{confirm.routeName}</div>
            <div className="text-[13px] text-ink/55">
              {confirm.cityName}
              {confirm.pointsCount != null && ` · ${confirm.pointsCount} ${plural(confirm.pointsCount, 'точка', 'точки', 'точек')}`}
            </div>
            {confirm.load && (
              <div className="mt-2 flex items-center gap-1.5 text-[13px] font-medium text-orange-700">
                <ClipboardList size={14} /> Накладная №{confirm.load.number} · {confirm.load.lines.length}{' '}
                {plural(confirm.load.lines.length, 'позиция', 'позиции', 'позиций')}
              </div>
            )}
          </div>
          <p className="mt-3 flex gap-2 text-[13px] text-ink/55">
            <Navigation size={15} className="mt-0.5 shrink-0" />
            Пока идёт смена, приложение отмечает ваш путь, а экран не гаснет. Точку можно отметить, только находясь рядом с ней.
          </p>
          {geoProblem && <p className="mt-3 rounded-xl bg-red-50 px-3 py-2.5 text-[13px] text-red-800">{geoProblem}</p>}
          <button type="button" className="btn-primary mt-5 w-full" onClick={start} disabled={starting}>
            {starting && <Loader2 size={18} className="animate-spin" />}
            {geoProblem ? 'Проверить ещё раз' : 'Начать смену'}
          </button>
          <button type="button" className="btn-ghost mt-2 w-full" onClick={() => setConfirm(null)} disabled={starting}>
            Отмена
          </button>
        </Sheet>
      )}
    </>
  );
}
